import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { Method } from 'axios';

import NetworkErrorMessage from 'shared/newComponents/NetworkErrorMessage';
import { INetworkStore } from 'shared/entities/store/networkStore';
import { IRootStore } from 'shared/entities/store/rootStore';
import {
  api as fetchJsonApi,
  ApiBlobParams,
  ApiCallErrorRaw,
  ApiCallResponse,
  ApiParams,
  ApiParamsShopback,
  BaseResponseCode,
  BinaryApiErrorCode,
  BinaryApiSuccess,
  requestBinaryApi,
  ResponseCodes
} from 'shared/entities/network';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { createShopbackData } from 'shared/entities/network/utils/helpers';
import { TranslationString } from 'shared/entities/localization';
import {
  BadRequestError,
  HttpError,
  NotFoundError
} from 'shared/models/errors';
import ForbiddenErrorMessage from 'shared/newComponents/ForbiddenErrorMessage';

const stringifyForSentry = (value: unknown): string | undefined => {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

export class NetworkStore implements INetworkStore {
  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  private handleApiError = <ErrorCode extends string>({
    endpoint,
    requestData,
    result,
    errorsMap,
    showExpectedError,
    showUnexpectedError,
    showAuthModal,
    expectedErrorCodes
  }: {
    endpoint: string;
    requestData: Record<string, any> | undefined;
    result: ApiCallResponse<
      Record<string, any>,
      ErrorCode,
      Record<string, any>
    >;
    errorsMap?: (code: ErrorCode, data?: unknown) => TranslationString;
    showExpectedError: boolean;
    showUnexpectedError: boolean;
    showAuthModal: boolean;
    expectedErrorCodes?: readonly ErrorCode[];
  }): void => {
    if (!result.isError || result.isCancelled) {
      return;
    }

    const sendToSentry = () => {
      if (!result.data) {
        return;
      }

      Sentry.withScope((scope) => {
        scope.setExtras({
          status: result.data?.status,
          code: result.data?.code,
          url: endpoint,
          requestData: stringifyForSentry(requestData),
          responseData: stringifyForSentry(result.data?.data)
        });
        if (
          result.data?.status === ResponseCodes.NOT_FOUND ||
          result.data?.code === BaseResponseCode.notFound
        ) {
          Sentry.captureException(new NotFoundError(endpoint));
          return;
        }
        if (
          result.data?.status === ResponseCodes.BAD_REQUEST ||
          result.data?.code === BaseResponseCode.badRequest
        ) {
          Sentry.captureException(new BadRequestError(endpoint));
          return;
        }
        Sentry.captureException(new HttpError(endpoint));
      });
    };

    if (result.data?.status === ResponseCodes.UNAUTHORIZED) {
      if (this.rootStore.userStore.isAuthorized && showAuthModal) {
        this.rootStore.userStore.resetAuth();
        this.rootStore.uiStore.openAuthorizedModal();
      }

      return;
    }

    if (result.data) {
      if (!expectedErrorCodes?.includes(result.data.code)) {
        if (result.data.code === BaseResponseCode.paymentRequired) {
          if (showUnexpectedError) {
            this.rootStore.paymentStore.showPaymentRequiredError();
          }

          return;
        }

        const isErrorExpected =
          errorsMap &&
          !!errorsMap(
            result.data.code,
            result.data.data
          )(this.rootStore.translationsStore.t);

        if (isErrorExpected) {
          if (showExpectedError) {
            const errorStr = errorsMap(result.data.code, result.data.data);
            this.rootStore.appNotificationsStore.open({
              type: AppNotificationType.error,
              title: errorStr
            });
          }
        } else {
          sendToSentry();

          if (showUnexpectedError) {
            this.showUnexpectedError(result.data, endpoint);
          }
        }
      }
    } else if (showUnexpectedError) {
      this.showUnexpectedError(result.data, endpoint);
    }
  };

  /**
   * Shared pipeline for JSON and binary requests: enrich payload, execute transport,
   * then run unified error handling (notifications, Sentry, auth modal).
   */
  private requestApi = async <
    ResponseData extends Record<string, any> = Record<string, any>,
    ErrorCode extends string = string,
    ErrorData extends Record<string, any> = Record<string, any>
  >(
    endpoint: string,
    {
      method = 'GET',
      data,
      config,
      multipartFormData,
      errorsMap,
      showExpectedError = true,
      showUnexpectedError = true,
      showAuthModal = true,
      expectedErrorCodes
    }: ApiParams<ErrorCode> = {
      method: 'GET',
      showExpectedError: true,
      showUnexpectedError: true
    },
    executeRequest: (
      endpoint: string,
      method: Method,
      requestData: Record<string, any>,
      config: ApiParams<ErrorCode>['config'],
      multipartFormData: boolean | undefined
    ) => Promise<ApiCallResponse<ResponseData, ErrorCode, ErrorData>>
  ): Promise<ApiCallResponse<ResponseData, ErrorCode, ErrorData>> => {
    const requestData = this.rootStore.createData(data);

    const result = await executeRequest(
      endpoint,
      method,
      requestData,
      config,
      multipartFormData
    );

    this.handleApiError<ErrorCode>({
      endpoint,
      requestData,
      result: result as ApiCallResponse<
        Record<string, any>,
        ErrorCode,
        Record<string, any>
      >,
      errorsMap,
      showExpectedError,
      showUnexpectedError,
      showAuthModal,
      expectedErrorCodes
    });

    return result;
  };

  apiShopback = async <
    ResponseData extends Record<string, any> = {},
    ErrorCode extends string = string,
    ErrorData extends Record<string, any> = {}
  >(
    endpoint: string,
    {
      method,
      data,
      config,
      multipartFormData,
      errorsMap,
      showExpectedError = true,
      showUnexpectedError = true
    }: ApiParamsShopback<ErrorCode> = {
      method: 'GET',
      showExpectedError: true,
      showUnexpectedError: true
    }
  ): Promise<ApiCallResponse<ResponseData, ErrorCode, ErrorData>> => {
    const shopbackData = createShopbackData(this.rootStore, data);

    const result = await this.api<ResponseData, ErrorCode, ErrorData>(
      endpoint,
      {
        method,
        data: shopbackData,
        config,
        multipartFormData,
        errorsMap,
        showExpectedError,
        showUnexpectedError,
        showAuthModal: false
      }
    );

    if (!result.isError) {
      return result;
    }

    if (result.data && result.data.status === ResponseCodes.UNAUTHORIZED) {
      const response = await this.rootStore.shopbackStore.reload();

      if (response.isError) {
        return result;
      }

      const shopbackData = createShopbackData(this.rootStore, data);

      return this.api<ResponseData, ErrorCode, ErrorData>(endpoint, {
        method,
        data: shopbackData,
        config,
        multipartFormData,
        errorsMap,
        showExpectedError,
        showUnexpectedError,
        showAuthModal: false
      });
    }

    return result;
  };

  api = async <
    ResponseData extends Record<string, any> = {},
    ErrorCode extends string = string,
    ErrorData extends Record<string, any> = {}
  >(
    endpoint: string,
    params: ApiParams<ErrorCode> = {
      method: 'GET',
      showExpectedError: true,
      showUnexpectedError: true
    }
  ): Promise<ApiCallResponse<ResponseData, ErrorCode, ErrorData>> => {
    return this.requestApi<ResponseData, ErrorCode, ErrorData>(
      endpoint,
      params,
      (requestEndpoint, method, requestData, config, multipartFormData) =>
        fetchJsonApi<ResponseData, ErrorCode, ErrorData>(
          requestEndpoint,
          method,
          requestData,
          config,
          multipartFormData
        )
    );
  };

  apiBlob = async <
    ErrorCode extends string = string,
    ErrorData extends Record<string, any> = {}
  >(
    endpoint: string,
    params: ApiBlobParams<ErrorCode> = {
      method: 'GET',
      showExpectedError: true,
      showUnexpectedError: true
    }
  ): Promise<
    ApiCallResponse<BinaryApiSuccess, BinaryApiErrorCode<ErrorCode>, ErrorData>
  > => {
    return this.requestApi<
      BinaryApiSuccess,
      BinaryApiErrorCode<ErrorCode>,
      ErrorData
    >(
      endpoint,
      params,
      (requestEndpoint, method, requestData, config, multipartFormData) =>
        requestBinaryApi<ErrorCode, ErrorData>(
          requestEndpoint,
          method,
          requestData,
          config,
          multipartFormData
        )
    );
  };

  showUnexpectedError = (
    error:
      | ({
          status: number;
        } & Omit<ApiCallErrorRaw<any, any>, 'status'>)
      | null,
    url: string
  ): void => {
    if (!error) {
      this.rootStore.appNotificationsStore.open({
        type: AppNotificationType.error,
        title: (t) =>
          t('NetworkStore.notifications.unknown', {
            ns: 'stores'
          }),
        timeout: 10000
      });

      return;
    }

    if (error.code === BaseResponseCode.forbidden || error.status === 403) {
      this.rootStore.appNotificationsStore.openForbiddenError({
        type: AppNotificationType.error,
        title: <ForbiddenErrorMessage />,
        timeout: 10000
      });
    } else {
      this.rootStore.appNotificationsStore.open({
        type: AppNotificationType.error,
        title: <NetworkErrorMessage url={url} error={error} />,
        timeout: 10000
      });
    }
  };
}
